import * as React from 'react';
import { cn } from '@/lib/utils';
import { AppCard } from './AppCard';
import { premiumTheme } from './tokens';

/**
 * SectionCard \u2014 Premium section container with header, divider, content.
 *
 * Structure:
 *   <header>  title + (optional subtitle) + (optional rightContent)
 *   <divider> hairline rule
 *   <content> children
 *
 * Header uses `h3` for accessibility. Title respects \u00a723.1 cap (14px).
 * Nested cards should opt into `variant="muted"` to avoid visual clutter.
 */

export interface SectionCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  rightContent?: React.ReactNode;
  /** Wrap content in padded container (default true) */
  padContent?: boolean;
  /** Content padding size token */
  contentPadding?: 'xs' | 'sm' | 'md' | 'lg';
  /** Visual variant forwarded to the underlying AppCard */
  variant?: 'card' | 'muted' | 'sunken';
  /** Remove the divider under the header */
  noDivider?: boolean;
}

export const SectionCard = React.forwardRef<HTMLDivElement, SectionCardProps>(
  (
    {
      title,
      subtitle,
      rightContent,
      padContent = true,
      contentPadding = 'md',
      variant = 'card',
      noDivider = false,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const hasHeader = Boolean(title || rightContent);

    return (
      <AppCard
        ref={ref}
        variant={variant}
        noPadding
        className={cn('flex flex-col', className)}
        {...props}
      >
        {hasHeader && (
          <header className="flex items-start justify-between gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              {title && (
                <h3 className={cn(premiumTheme.typography.title, 'truncate')}>
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className={cn(premiumTheme.typography.subtitle, 'mt-0.5')}>
                  {subtitle}
                </p>
              )}
            </div>
            {rightContent && (
              <div className="flex items-center gap-2 flex-shrink-0">
                {rightContent}
              </div>
            )}
          </header>
        )}

        {hasHeader && !noDivider && (
          <div className="border-t border-border-hairline" />
        )}

        <div
          className={cn(
            padContent && premiumTheme.padding[contentPadding],
            'flex-1 min-w-0',
          )}
        >
          {children}
        </div>
      </AppCard>
    );
  },
);
SectionCard.displayName = 'SectionCard';
